import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getMainButtonName } from '../store/sharedStateSlice';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getCurrentChannel } from '../config';
import useCustomFetchApi from '../utils/customFetchApi';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';

function Header({ onParticipateClick, onHowItWorksClick, isParticipateShown = true, isCompact = false }) {
  // Feature flag: show CTA price badge (smaller, inline)
  const SHOW_CTA_PRICE = true;
  const buttonNameKey = useSelector(getMainButtonName);
  const { t, i18n } = useTranslation();
  const { currentTheme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showMobileCTA, setShowMobileCTA] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [ctaPrice, setCtaPrice] = useState({ currency: null, amount: null, loading: false });
  const api = useCustomFetchApi();

  const switchLanguageFromMenu = (langKey) => {
    const langCode = langKey === 'us' ? 'en' : langKey; // map display key to code
    try { i18n.changeLanguage(langCode); } catch (_) {}
    const segments = (location.pathname || '/').split('/').filter(Boolean);
    const known = ['en', 'ar', 'my'];
    if (segments.length && known.includes(segments[0])) segments.shift();
    const newPath = `/${langCode}${segments.length ? '/' + segments.join('/') : ''}`;
    navigate(newPath);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Show sticky header CTA after scrolling past hero (25% of viewport)
      if (scrollY > windowHeight * 0.25) {
        setShowStickyCTA(true);
      } else {
        setShowStickyCTA(false);
      }

      // Show mobile bottom CTA after 25% scroll on mobile
      if (window.innerWidth <= 768) {
        if (scrollY > windowHeight * 0.25) {
          setShowMobileCTA(true);
        } else {
          setShowMobileCTA(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep local state in sync with actual fullscreen changes
  useEffect(() => {
    const syncFullscreen = () => {
      try {
        const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
        setIsFullscreen(!!fsEl);
      } catch (_) { /* ignore */ }
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
    document.addEventListener('msfullscreenchange', syncFullscreen);
    syncFullscreen();
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
      document.removeEventListener('msfullscreenchange', syncFullscreen);
    };
  }, []);

  const requestFullscreen = () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) return el.requestFullscreen();
      if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
      if (el.msRequestFullscreen) return el.msRequestFullscreen();
    } catch (_) { /* ignore */ }
  };

  const exitFullscreen = () => {
    try {
      if (document.exitFullscreen) return document.exitFullscreen();
      if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
      if (document.msExitFullscreen) return document.msExitFullscreen();
    } catch (_) { /* ignore */ }
  };

  const handleToggleFullscreen = async () => {
    try {
      if (isFullscreen) {
        await exitFullscreen();
        trackEvent('fullscreen_toggle', { action: 'exit', source: 'menu' });
      } else {
        await requestFullscreen();
        trackEvent('fullscreen_toggle', { action: 'enter', source: 'menu' });
      }
    } catch (_) { /* ignore */ }
    // Close menu after action for a cleaner UX
    setIsMenuOpen(false);
  };

  // Resolve CTA key considering theme-specific copy without overriding the flow logic
  const resolvedCtaKey = (() => {
    const themeId = currentTheme?.id;
    const base = buttonNameKey; // 'buttons.participate' | 'buttons.continue'
    if (themeId === 'iraq' || themeId === 'malaysia') {
      if (base === 'buttons.participate') return `buttons.${themeId}.participate`;
      if (base === 'buttons.continue') return `buttons.${themeId}.continue`;
    }
    return base;
  })();

  // Fetch dynamic spot price for current channel to show inline with CTA
  useEffect(() => {
    let mounted = true;
    const channel = getCurrentChannel();
    const load = async () => {
      try {
        setCtaPrice(p => ({ ...p, loading: true }));
        const data = await api.get(`/spot-price?channel=${channel}`, { showNotification: false });
        if (!mounted) return;
        const currency = data?.dataValues?.currency || data?.currency || null;
        const amount = data?.dataValues?.spot_price || data?.spot_price || null;
        setCtaPrice({ currency, amount, loading: false });
      } catch (_) {
        if (!mounted) return;
        setCtaPrice({ currency: null, amount: null, loading: false });
      }
    };
    load();
    return () => { mounted = false; };
  }, [location.pathname]);

  const currencySymbol = (cur) => {
    if (!cur) return '';
    switch((cur || '').toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CNY': return '¥';
      case 'KRW': return '₩';
      case 'INR': return '₹';
      case 'RUB': return '₽';
      case 'BRL': return 'R$';
      case 'CAD': return 'C$';
      case 'AUD': return 'A$';
      case 'CHF': return 'CHF';
      case 'SEK': return 'kr';
      case 'NOK': return 'kr';
      case 'DKK': return 'kr';
      case 'PLN': return 'zł';
      case 'CZK': return 'Kč';
      case 'HUF': return 'Ft';
      case 'TRY': return '₺';
      case 'ZAR': return 'R';
      case 'MXN': return '$';
      case 'SGD': return 'S$';
      case 'HKD': return 'HK$';
      case 'NZD': return 'NZ$';
      case 'THB': return '฿';
      case 'MYR': return 'RM';
      case 'IDR': return 'Rp';
      case 'PHP': return '₱';
      case 'VND': return '₫';
      case 'IQD': return 'ع.د';
      case 'SAR': return 'ر.س';
      case 'AED': return 'د.إ';
      case 'KWD': return 'د.ك';
      case 'BHD': return 'د.ب';
      case 'OMR': return 'ر.ع';
      case 'QAR': return 'ر.ق';
      case 'JOD': return 'د.أ';
      case 'LBP': return 'ل.ل';
      case 'EGP': return 'ج.م';
      case 'MAD': return 'د.م';
      case 'DZD': return 'د.ج';
      case 'TND': return 'د.ت';
      case 'LYD': return 'د.ل';
      default: return cur;
    }
  };

  const actionPriceSuffix = (() => {
    if (ctaPrice.loading) return null;
    if (!ctaPrice.amount || !ctaPrice.currency) return null;
    const symbol = currencySymbol(ctaPrice.currency);
    const decimalsByCurrency = {
      JPY: 0, KRW: 0, VND: 0,
      IQD: 0, KWD: 3, BHD: 3, OMR: 3,
    };
    const cur = (ctaPrice.currency || '').toUpperCase();
    const decimals = typeof decimalsByCurrency[cur] === 'number' ? decimalsByCurrency[cur] : 2;
    const amountNum = Number(ctaPrice.amount);
    const amountStr = Number.isFinite(amountNum) ? amountNum.toFixed(decimals) : String(ctaPrice.amount);
    return (
      <>
        <span className="price-suffix" aria-label={`Price ${symbol} ${amountStr}`}>
          <span className="price-dot" aria-hidden="true" />
          <span className="currency">{symbol}&nbsp;</span>
          {/* <span className="space" aria-hidden="true">&nbsp;</span> */}
          <span className="amount">{amountStr}</span>
        </span>
      </>
    );
  })();

  return (
    <>
      {/* Menu button moved outside header to avoid stacking context issues */}
      <button
        className={`menu-button ${isMenuOpen ? 'open' : ''}`}
        aria-label="Open menu"
        onClick={() => setIsMenuOpen(v => !v)}
      >
        <span />
        <span />
        <span />
      </button>
      
      {isMenuOpen && (
        <>
          <div className="menu-overlay" onClick={() => setIsMenuOpen(false)} />
          <div className="menu-panel">
            <div className="menu-section">
              <h4>Language</h4>
              <div className="menu-language-row">
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('us')}>US</button>
                <button type="button" className="menu-link menu-language-row" onClick={() => switchLanguageFromMenu('my')}>MY</button>
              </div>
            </div>
            <div className="menu-section">
              <h4>Account</h4>
              {isAuthenticated() ? (
                <Link to={`/${i18n.language}/${getCurrentChannel()}/dashboard`} className="menu-link" onClick={() => setIsMenuOpen(false)}>
                  👋 {user?.nickname || 'Dashboard'}
                </Link>
              ) : (
                <Link to="/login" className="menu-link" onClick={() => setIsMenuOpen(false)}>
                  Login
                </Link>
              )}
            </div>
            <div className="menu-section">
              <button type="button" className="menu-link" onClick={handleToggleFullscreen}>
                {isFullscreen ? t('buttons.fullscreenExit') : t('buttons.fullscreenEnter')}
              </button>
            </div>
          </div>
        </>
      )}

  <header className={`App-header ${isCompact ? 'compact' : ''}`}>

        <h1>
          {currentTheme.id === 'iraq'
            ? t('header.iraq.title')
            : currentTheme.id === 'malaysia'
              ? t('header.malaysia.title')
              : t('header.title')
          }
        </h1>
        <span className="subtitle">
          {t('header.subtitle')}
        </span>
        <span className="subtitle protect-tagline" style={{display:'block', marginTop:'0.35rem', fontSize:'0.9rem', color:'#cfe9ff'}}>
          {t('header.protectTagline', 'We will protect your memory and pass it to the next generations')}
        </span>
        {isParticipateShown && !isAuthenticated() && (
          <div className="cta-container">
            <button className="submit-button cta-button" onClick={() => { trackEvent('cta_click', { location: 'hero' }); onParticipateClick(); }}>
              <span className="cta-label">{t(resolvedCtaKey)}</span>
              {SHOW_CTA_PRICE && actionPriceSuffix && (
                <span className="cta-price-row">{actionPriceSuffix}</span>
              )}
            </button>
            <div className="privacy-note-under-cta" style={{ marginTop: '6px', fontSize: '0.8rem', color: '#cfe9ff', opacity: 0.9, textAlign: 'center' }}>
              {t('header.privacyNote', 'You can keep your profile private (only visible to you)')}
            </div>
            <a
              href="#"
              className="how-it-works-link"
              onClick={(e) => {
                e.preventDefault();
                trackEvent('how_it_works_click', { location: 'hero' });
                onHowItWorksClick();
              }}
            >
              {t('buttons.howItWorks')}
            </a>
          </div>
        )}

      </header>

      {/* Sticky Header CTA */}
      {!isAuthenticated() && (
        <div className={`sticky-header-cta ${showStickyCTA ? 'visible' : ''}`}>
          <button className="submit-button sticky-cta-button" onClick={() => { trackEvent('cta_click', { location: 'sticky' }); onParticipateClick(); }}>
            <span className="cta-label">{t(resolvedCtaKey)}</span>
            {SHOW_CTA_PRICE && actionPriceSuffix && (
              <span className="cta-price-row">{actionPriceSuffix}</span>
            )}
          </button>
        </div>
      )}

      {/* Mobile Bottom CTA */}
      {!isAuthenticated() && (
        <div className={`mobile-bottom-cta ${showMobileCTA ? 'visible' : ''}`}>
          <button className="submit-button mobile-cta-button" onClick={() => { trackEvent('cta_click', { location: 'mobile' }); onParticipateClick(); }}>
            <span className="cta-label">{t(resolvedCtaKey)}</span>
            {SHOW_CTA_PRICE && actionPriceSuffix && (
              <span className="cta-price-row">{actionPriceSuffix}</span>
            )}
          </button>
        </div>
      )}
    </>
  );
}

export default Header;
